import { createContext, type ReactNode, useContext } from 'react';
import { DEMO_JWT, DEMO_USER_ID } from '@/lib/demoMode';
import {
  demoDisplayName,
  getDemoSessionSnapshot,
  setDemoProfile,
} from '@/lib/demoSession';
import type { PublicAuthConfig } from './config';

/**
 * The runtime-auth contexts and their offline stand-in, with NO Stytch import.
 *
 * `runtimeAuth.tsx` statically imports `@stytch/react/b2b`, plus `BootScreen`
 * and the boot-config fetch. That is
 * correct for the app, and wrong for `.storybook/preview.ts`, which needs
 * exactly two context values and pulls the whole SDK graph into every story
 * bundle to get them — 80 story files and the jsdom unit project, none of which
 * ever construct a Stytch client.
 *
 * So the leaf lives here and `runtimeAuth.tsx` imports it. Every existing
 * importer keeps working: `runtimeAuth.tsx` re-exports all of this, and the
 * dependency runs leaf → app, never the reverse.
 *
 * The two contexts stay module-private on purpose: a consumer gets
 * `DemoRuntimeAuthProvider`, not a seam to hang an arbitrary SDK on. A story
 * needing a different session shape should take the fields as props, the way
 * `ActivityBar` does.
 */

/** One organization the signed-in person can enter, personal workspace included. */
export interface Workspace {
  /** The provider's organization id — what `switchWorkspace` takes. */
  id: string;
  name: string;
  /** The session's own organization. Exactly one entry is active. */
  isActive: boolean;
}

/**
 * `switched`: the browser now holds a session in the chosen organization.
 * `needs_sign_in`: the organization asks for a step this app has no screen
 * for (a second factor, or an SSO-only primary) and the provider answered with
 * an intermediate session instead — and dropped the old session with it, so
 * the person is signed out of where they came from, not parked there. The
 * sign-in page can finish that step. No organization requires either today.
 */
export type WorkspaceSwitchOutcome = 'switched' | 'needs_sign_in';

/**
 * Who the provider session belongs to, as the SDK holds it synchronously. A
 * display and cache-key shape, never an authorization source: canonical
 * identity and organization role come from `GET /auth/current`.
 */
export interface AuthSubject {
  /** The B2B `member_id`. Partitions the canonical-identity cache. */
  id: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
}

export interface RuntimeAuthSdk {
  config: PublicAuthConfig;
  isInitialized: boolean;
  /** The session's subject, or `null` when the browser holds no session. */
  subject: AuthSubject | null;
  isEmailVerified: boolean;
  getSessionJwt: () => string | undefined;
  refreshSession: () => Promise<void>;
  revokeSession: () => Promise<void>;
  getAuthenticationToken: () => string | undefined;
  updateProfile: (firstName: string, lastName: string) => Promise<void>;
  /** Guard queued account writes against a change of the provider session. */
  getCurrentSubjectId?: () => string | undefined;
  /**
   * This session can be exchanged for one in another organization the person
   * belongs to. A B2B session is scoped to one organization; the demo session
   * has a single static organization and reports false. The request's
   * workspace is the token's organization claim and nothing else, so switching
   * is a new session, not a header — `switchWorkspace` exchanges the current
   * session for one in the chosen organization and the caller reloads the app
   * on top of it.
   */
  canSwitchWorkspace: boolean;
  listWorkspaces: () => Promise<Workspace[]>;
  switchWorkspace: (workspaceId: string) => Promise<WorkspaceSwitchOutcome>;
}

export const RuntimeAuthConfigContext = createContext<PublicAuthConfig | null>(
  null,
);
export const RuntimeAuthSdkContext = createContext<RuntimeAuthSdk | null>(null);

const demoConfig: PublicAuthConfig = {
  schema_version: 1,
  project_id: 'demo',
  public_token: 'demo',
  environment: 'test',
};

// Profile fields read through getters rather than being frozen literals: the
// account page edits them, and anything reading the SDK directly has to see the
// same value `useDemoAuth` is rendering. The demo hosts no Stytch widget.
const demoSubject: AuthSubject = {
  id: DEMO_USER_ID,
  email: 'demo@khorium.ai',
  get name() {
    return demoDisplayName(getDemoSessionSnapshot());
  },
  get firstName() {
    return getDemoSessionSnapshot().firstName;
  },
  get lastName() {
    return getDemoSessionSnapshot().lastName;
  },
};

const demoSdk: RuntimeAuthSdk = {
  config: demoConfig,
  isInitialized: true,
  subject: demoSubject,
  isEmailVerified: true,
  canSwitchWorkspace: false,
  getSessionJwt: () => DEMO_JWT,
  refreshSession: async () => undefined,
  revokeSession: async () => undefined,
  getAuthenticationToken: () => undefined,
  updateProfile: async (firstName, lastName) => {
    setDemoProfile(firstName, lastName);
  },
  listWorkspaces: async () => {
    throw new Error('Demo workspace switching is unavailable');
  },
  switchWorkspace: async () => {
    throw new Error('Demo workspace switching is unavailable');
  },
};

export function useRuntimeAuthConfig(): PublicAuthConfig {
  const config = useContext(RuntimeAuthConfigContext);
  if (!config) throw new Error('Runtime auth config is unavailable');
  return config;
}

export function useRuntimeAuthSdk(): RuntimeAuthSdk {
  const sdk = useContext(RuntimeAuthSdkContext);
  if (!sdk) throw new Error('Runtime auth SDK is unavailable');
  return sdk;
}

/**
 * The app's auth stack in its offline form — a global Storybook decorator, and
 * the demo-mode branch of `RuntimeAuthProvider`.
 *
 * Without it, `useAuth()` resolves to `useRuntimeAuth` under Storybook
 * (`isDemoMode()` is false there) and hard-throws "Runtime auth SDK is
 * unavailable" when nothing provides the SDK context. Every story rendering an
 * auth-consuming component then paints the router's error boundary instead of
 * the component, which is a crash the accessibility gate measures as a contrast
 * violation on the error text (ENG-1461).
 */
export function DemoRuntimeAuthProvider({ children }: { children: ReactNode }) {
  return (
    <RuntimeAuthConfigContext.Provider value={demoConfig}>
      <RuntimeAuthSdkContext.Provider value={demoSdk}>
        {children}
      </RuntimeAuthSdkContext.Provider>
    </RuntimeAuthConfigContext.Provider>
  );
}
